/** Payment provider rules shared by the checkout UI and the API. */
import { cardUnitArs, transferUnitArs } from "./money";
import type { CouponKind } from "./coupons";
import { shippingUsdCents } from "./shipping";

export const PAYMENT_PROVIDERS = ["mercadopago", "stripe", "paypal", "transfer", "crypto"] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

/** Paid outside a gateway (bank transfer, crypto to the shop's wallet): confirmed by hand in the admin. */
export const MANUAL_PROVIDERS: readonly PaymentProvider[] = ["transfer", "crypto"];
export const isManualProvider = (p: PaymentProvider | null | undefined): boolean => !!p && MANUAL_PROVIDERS.includes(p);
/** Default discount (%) on the products for manual payments (setting manual_payment_discount_pct). */
export const MANUAL_DISCOUNT_PCT = 5;
/** Manual payments keep the stock reserved longer than gateway checkouts. */
export const MANUAL_RESERVATION_MINUTES = 120;

export type ChargeCurrency = "ARS" | "USD";

/** Mercado Pago charges in pesos; Stripe and PayPal charge the USD prices directly (PayPal has no ARS). */
export const PROVIDER_CURRENCY: Record<PaymentProvider, ChargeCurrency> = {
  mercadopago: "ARS",
  stripe: "USD",
  paypal: "USD",
  transfer: "ARS",
  crypto: "USD",
};

/** Minutes a pending order keeps its stock reserved while the customer pays. */
export const RESERVATION_MINUTES = 30;

export function isPaymentProvider(value: unknown): value is PaymentProvider {
  return typeof value === "string" && (PAYMENT_PROVIDERS as readonly string[]).includes(value);
}

export interface Charge {
  currency: ChargeCurrency;
  /** Amount in minor units (centavos / cents), discounts and shipping included. */
  amountMinor: number;
  /**
   * Discount on the products for paying outside the card gateway, in the same minor units: in pesos
   * the bank-transfer price vs. the Mercado Pago price, in dollars the crypto discount (0 when none).
   */
  discountMinor: number;
  /** Coupon discount on the products, same minor units (0 when none). */
  couponMinor: number;
  /** ARS per USD used for the conversion (pesos charges), or null for USD charges. */
  fxRate: number | null;
}

export interface ChargeLine {
  /** USD cents. */
  unitPrice: number;
  /** Fixed peso price set by an admin: the bank-transfer price (whole ARS); null/absent → derived from unitPrice. */
  unitPriceArs?: number | null;
  quantity: number;
}

/** A coupon already validated for this order: `eligible[i]` says whether it covers lines[i]. */
export interface ChargeCoupon {
  kind: CouponKind;
  value: number;
  eligible: boolean[];
}

export interface ChargeOptions {
  /** Mercado Pago's fee (%): card prices are transfer prices grossed up by it. */
  mpFeePct: number;
  /** Crypto discount (%) on the dollar products (setting manual_payment_discount_pct). */
  cryptoDiscountPct?: number;
  coupon?: ChargeCoupon | null;
}

/** Peso price of one line's unit for a provider: bank transfer pays the transfer price, the rest the card price. */
export function lineUnitArs(l: ChargeLine, provider: PaymentProvider, rate: number, mpFeePct: number): number {
  return provider === "transfer" ? transferUnitArs(l.unitPrice, rate, l.unitPriceArs, mpFeePct) : cardUnitArs(l.unitPrice, rate, l.unitPriceArs, mpFeePct);
}

function couponOff(coupon: ChargeCoupon | null | undefined, eligibleMinor: number, fixedMinor: number): number {
  if (!coupon || eligibleMinor <= 0) return 0;
  return coupon.kind === "percent" ? Math.floor((eligibleMinor * coupon.value) / 100) : Math.min(fixedMinor, eligibleMinor);
}

/**
 * What the provider will charge. Prices are USD cents.
 *   - Pesos: Mercado Pago charges the card price (dólar + 5%, or the fixed transfer price plus MP's fee),
 *     bank transfer the transfer price; `discountMinor` records the difference. Rounded to $10 per unit.
 *   - Dollars: Stripe/PayPal/crypto charge the USD prices; crypto takes `cryptoDiscountPct` off.
 *   - A coupon comes off the products it covers first; the crypto discount applies to what is left.
 * Shipping is priced in pesos: charged as is in pesos, or at the day's rate in dollars; never discounted.
 */
export function computeCharge(lines: ChargeLine[], provider: PaymentProvider, usdArsRate: number | null, shippingArs: number, opts: ChargeOptions): Charge {
  for (const l of lines) {
    if (!Number.isInteger(l.unitPrice) || l.unitPrice < 0 || !Number.isInteger(l.quantity) || l.quantity < 1) throw new RangeError("invalid line");
    if (l.unitPriceArs != null && (!Number.isInteger(l.unitPriceArs) || l.unitPriceArs <= 0)) throw new RangeError("invalid line");
  }
  if (!Number.isInteger(shippingArs) || shippingArs < 0) throw new RangeError("invalid shipping");
  const cryptoPct = provider === "crypto" ? opts.cryptoDiscountPct ?? 0 : 0;
  if (!Number.isFinite(cryptoPct) || cryptoPct < 0 || cryptoPct >= 100) throw new RangeError("invalid discount");
  if (!Number.isFinite(opts.mpFeePct) || opts.mpFeePct < 0 || opts.mpFeePct >= 50) throw new RangeError("invalid fee");
  const coupon = opts.coupon ?? null;
  if (coupon && coupon.eligible.length !== lines.length) throw new RangeError("invalid coupon");
  const validRate = usdArsRate != null && Number.isFinite(usdArsRate) && usdArsRate > 0;
  const eligible = (i: number) => coupon?.eligible[i] === true;

  if (PROVIDER_CURRENCY[provider] === "USD") {
    if ((shippingArs > 0 || coupon?.kind === "fixed") && !validRate) throw new RangeError("usdArsRate must be positive");
    const products = lines.reduce((n, l) => n + l.unitPrice * l.quantity, 0);
    const eligibleCents = lines.reduce((n, l, i) => n + (eligible(i) ? l.unitPrice * l.quantity : 0), 0);
    const off = couponOff(coupon, eligibleCents, coupon?.kind === "fixed" ? Math.floor((coupon.value * 100) / usdArsRate!) : 0);
    const discount = Math.floor(((products - off) * cryptoPct) / 100);
    const shipping = shippingArs > 0 ? shippingUsdCents(shippingArs, usdArsRate!) : 0;
    return { currency: "USD", amountMinor: products - off - discount + shipping, discountMinor: discount, couponMinor: off, fxRate: shippingArs > 0 || off > 0 ? usdArsRate : null };
  }
  if (!validRate) throw new RangeError("usdArsRate must be positive");
  const fee = opts.mpFeePct;
  const card = lines.reduce((n, l) => n + cardUnitArs(l.unitPrice, usdArsRate!, l.unitPriceArs, fee) * l.quantity, 0);
  const paid = lines.map((l) => lineUnitArs(l, provider, usdArsRate!, fee) * l.quantity);
  const products = paid.reduce((n, x) => n + x, 0);
  const off = couponOff(coupon, paid.reduce((n, x, i) => n + (eligible(i) ? x : 0), 0), coupon?.kind === "fixed" ? coupon.value : 0);
  return { currency: "ARS", amountMinor: (products - off + shippingArs) * 100, discountMinor: (card - products) * 100, couponMinor: off * 100, fxRate: usdArsRate };
}

/** "1234.50" — decimal string format used by Mercado Pago and PayPal APIs. */
export function minorToDecimal(amountMinor: number): string {
  return (amountMinor / 100).toFixed(2);
}

export function parseRate(raw: string | undefined | null): number | null {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}
